import React, { useState } from 'react'
import { AdCreative, LeadCard, LeadCardCreateDTO } from '@marketing-milk/interfaces'
import { Autocomplete, TextField, Tooltip } from '@mui/material'
import { leadCardService, useGetBusiness } from '@marketing-milk/frontend'
import { toast } from 'react-toastify'
import { BlankPageLayout } from 'components/BlankPageLayout'
import { ConfirmationModal } from 'components/ConfirmationModal'
import { LeadCardWizard } from 'pages/business/lead-cards/wizard/LeadCardWizard'
import { LeadFormChip } from './LeadFormChip'
import { useLeadCardCandidates } from './useLeadCardCandidates'

type Props = {
  businessID: number
  campaignName?: string
  creatives: AdCreative[]
  declaredForms: LeadCard[]
  setDeclaredForms: (forms: LeadCard[]) => void
  onUnassignForm: (form: LeadCard) => Promise<void>
  onBuildingChange?: (building: boolean) => void
  actionsContainer?: HTMLElement | null
}

export const DeclareLeadFormsStep = ({
  businessID,
  campaignName,
  creatives,
  declaredForms,
  setDeclaredForms,
  onUnassignForm,
  onBuildingChange,
  actionsContainer,
}: Props) => {
  const business = useGetBusiness()
  const { leadCards, truncated } = useLeadCardCandidates(businessID)
  const [pendingRemoval, setPendingRemoval] = useState<LeadCard>()
  const [searchInput, setSearchInput] = useState('')
  const [building, setBuildingState] = useState<'copy' | 'blank' | false>(false)
  const setBuilding = (mode: 'copy' | 'blank' | false) => {
    setBuildingState(mode)
    onBuildingChange?.(!!mode)
  }

  const countForForm = (form: LeadCard) =>
    creatives.filter(c => c.leadCardUsed?.id === form.id).length

  const removeForm = (form: LeadCard) => {
    if (countForForm(form) > 0) {
      setPendingRemoval(form)
      return
    }
    setDeclaredForms(declaredForms.filter(f => f.id !== form.id))
  }

  // Already-declared forms are excluded from the search results — picking one again would just
  // be a confusing no-op — and shown as their own row above the box instead of living inside it,
  // so the input's height and label position stay put as the batch grows.
  const availableOptions = (leadCards ?? []).filter(
    form => !declaredForms.some(declared => declared.id === form.id)
  )

  // Creation needs a Facebook Page to hang the form off; without one the API 403s. Disabled with
  // the reason attached rather than hidden, since a missing button reads as a bug rather than as
  // a business that isn't connected yet.
  const canCreate = !!business?.fbPageID

  const createForm = async (leadCardDTO: LeadCardCreateDTO) => {
    const newLeadCard = await leadCardService.create(businessID, { ...leadCardDTO })
    // Declaring it is the whole point of building it here — making the AE go find it in the
    // search box afterwards would put back most of the round trip this step removes.
    setDeclaredForms([...declaredForms, newLeadCard])
    setBuilding(false)
    toast.success(`"${newLeadCard.formName}" created and added to this batch`)
  }

  if (building) {
    return (
      <div className="w-full p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold">New Lead Form</h2>
          {!onBuildingChange && (
            <button
              type="button"
              className="rounded-md px-3 py-1.5 text-sm font-semibold bg-white text-gray-700 border hover:bg-gray-50"
              onClick={() => setBuilding(false)}
            >
              Cancel
            </button>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Saving adds this form to the batch and brings you back to the list.
        </p>
        <LeadCardWizard
          onSave={createForm}
          startFromForms={building === 'copy' ? leadCards ?? [] : undefined}
          actionsContainer={actionsContainer}
          onCancelCreation={onBuildingChange ? () => setBuilding(false) : undefined}
        />
      </div>
    )
  }

  return (
    <BlankPageLayout
      className="max-w-3xl"
      title={
        campaignName
          ? `Declare This Batch's Lead Forms — ${campaignName}`
          : "Declare This Batch's Lead Forms"
      }
      subtitle="Search for and add every lead form this batch of ads uses. The next step assigns ads to these forms."
    >
      {declaredForms.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 w-full">
          {declaredForms.map(form => (
            <LeadFormChip key={form.id} form={form} onRemove={() => removeForm(form)} />
          ))}
        </div>
      )}

      {/* Beside the box at all times, not only in the no-results state: someone who already
          knows they need a new form shouldn't have to type a name that doesn't exist to find
          the button. */}
      <div className="flex flex-wrap w-full gap-2 items-center">
        <Autocomplete
          fullWidth
          sx={{ minWidth: 220, flex: '1 1 280px' }}
          options={availableOptions}
          value={null}
          inputValue={searchInput}
          onInputChange={(_, value) => setSearchInput(value)}
          onChange={(_, selected) => {
            if (!selected) return
            setDeclaredForms([...declaredForms, selected])
            setSearchInput('')
          }}
          getOptionLabel={form => form.formName}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label="Search lead forms"
              placeholder="Type a form name…"
            />
          )}
        />

        <Tooltip
          title={
            canCreate
              ? leadCards?.length
                ? ''
                : 'No existing forms are available to copy. Start blank to create a new form.'
              : "This business has no Facebook Page connected, so new lead forms can't be created for it yet."
          }
        >
          <span>
            <button
              type="button"
              disabled={!canCreate || !leadCards?.length}
              onClick={() => setBuilding('copy')}
              className="whitespace-nowrap rounded-md px-4 py-3.5 font-semibold text-sm bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Copy existing form
            </button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            canCreate
              ? ''
              : "This business has no Facebook Page connected, so new lead forms can't be created for it yet."
          }
        >
          <span>
            <button
              type="button"
              disabled={!canCreate}
              onClick={() => setBuilding('blank')}
              className="whitespace-nowrap rounded-md px-3 py-3.5 font-semibold text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Start blank
            </button>
          </span>
        </Tooltip>
      </div>

      {truncated && (
        <p className="text-sm text-gray-500 mt-2">
          Showing this business's 150 most recently created forms.
        </p>
      )}

      <ConfirmationModal
        openState={[!!pendingRemoval, open => !open && setPendingRemoval(undefined)]}
        warningText={
          pendingRemoval &&
          `Removing this form will unassign ${countForForm(pendingRemoval)} ad${
            countForForm(pendingRemoval) === 1 ? '' : 's'
          } — everywhere, not just this campaign.`
        }
        confirmButtonText="Remove Form"
        confirmButtonType="delete"
        onConfirm={async () => {
          if (!pendingRemoval) return
          const removed = pendingRemoval
          await onUnassignForm(removed)
          setDeclaredForms(declaredForms.filter(f => f.id !== removed.id))
          setPendingRemoval(undefined)
        }}
      />
    </BlankPageLayout>
  )
}
